"use client";

import { useEffect, useState, useTransition } from "react";
import { Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  FAQ_ANSWER_LIMIT,
  FAQ_QUESTION_LIMIT,
  type FaqTargetKind,
  type FaqTargets,
} from "@/lib/inbox/faq-targets";
import {
  getFaqTargets,
  prepareFaqFromEmail,
  saveFaqFromEmailAction,
  type FaqVenueOption,
} from "./actions";

const LANGUAGE_NAMES: Record<string, string> = {
  da: "Danish",
  en: "English",
  no: "Norwegian",
  sv: "Swedish",
};

const KIND_LABELS: Record<FaqTargetKind, string> = {
  venue: "Venue",
  show: "Show",
  add_on: "Add-on",
  shop_item: "Shop item",
};

const field =
  "w-full rounded border border-gray-200 bg-white px-3 py-2 text-sm focus:border-gray-400 focus:outline-none disabled:opacity-60";

const itemsOf = (targets: FaqTargets | null, kind: FaqTargetKind) =>
  !targets || kind === "venue"
    ? []
    : {
        show: targets.shows,
        add_on: targets.addOns,
        shop_item: targets.shopItems,
      }[kind];

/** The email's language first, then the venue's others. */
const tabsFor = (targets: FaqTargets | null, lang: string) => {
  const languages = targets?.languages ?? [lang];
  return languages.includes(lang)
    ? [lang, ...languages.filter((l) => l !== lang)]
    : languages;
};

/**
 * "Add to FAQ" on one message of an inbox thread: the AI drafts a public FAQ
 * from that message's question and answer (a customer email with the staff
 * reply after it, or a staff reply with the email before it) in each of the
 * venue's languages, staff choose where it goes and edit each language, and
 * it is saved as an active FAQ in every language filled in. Closed while
 * `messageId` is null.
 */
export function AddToFaqDialog({
  conversationId,
  messageId,
  onClose,
}: {
  conversationId: string;
  messageId: string | null;
  onClose: () => void;
}) {
  const open = messageId !== null;
  const [loading, setLoading] = useState(false);
  const [venues, setVenues] = useState<FaqVenueOption[]>([]);
  const [venueId, setVenueId] = useState<number | null>(null);
  const [targets, setTargets] = useState<FaqTargets | null>(null);
  const [kind, setKind] = useState<FaqTargetKind>("venue");
  const [itemId, setItemId] = useState<number | null>(null);
  const [questions, setQuestions] = useState<Record<string, string>>({});
  const [answers, setAnswers] = useState<Record<string, string>>({});
  /** The email's language. */
  const [lang, setLang] = useState("en");
  const [tab, setTab] = useState("en");
  const [drafted, setDrafted] = useState(false);
  const [draftError, setDraftError] = useState<string>();
  const [saving, startSaving] = useTransition();

  // A fresh draft each time the dialog opens: the thread may have moved on.
  useEffect(() => {
    if (!messageId) return;
    let cancelled = false;
    setLoading(true);
    setQuestions({});
    setAnswers({});
    setKind("venue");
    setItemId(null);
    setDrafted(false);
    setDraftError(undefined);

    prepareFaqFromEmail(conversationId, messageId).then((result) => {
      if (cancelled) return;
      setLoading(false);
      if (!result.success || !result.data) {
        toast.error(result.error ?? "Could not open Add to FAQ");
        onClose();
        return;
      }
      const setup = result.data;
      setVenues(setup.venues);
      setVenueId(setup.venueId);
      setTargets(setup.targets);
      setLang(setup.lang);
      setTab(tabsFor(setup.targets, setup.lang)[0]);
      setDraftError(setup.draftError);
      setQuestions(setup.questions);
      setAnswers(setup.answers);
      if (setup.draft) {
        setKind(setup.draft.kind);
        setItemId(setup.draft.itemId);
        setDrafted(true);
      }
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageId, conversationId]);

  const chooseVenue = (id: number | null) => {
    setVenueId(id);
    setTargets(null);
    setKind("venue");
    setItemId(null);
    if (!id) return;
    getFaqTargets(id, lang).then((result) => {
      if (result.success && result.data) {
        setTargets(result.data);
        setTab(tabsFor(result.data, lang)[0]);
      } else {
        toast.error(result.error ?? "Could not load the venue's items");
      }
    });
  };

  const tabs = tabsFor(targets, lang);
  const items = itemsOf(targets, kind);
  const filled = (l: string) =>
    Boolean(questions[l]?.trim() && answers[l]?.trim());
  const half = (l: string) =>
    !filled(l) && Boolean(questions[l]?.trim() || answers[l]?.trim());
  const filledTabs = tabs.filter(filled);
  const canSave =
    !loading &&
    !saving &&
    venueId !== null &&
    filledTabs.length > 0 &&
    !tabs.some(half) &&
    (kind === "venue" || itemId !== null);

  const save = () =>
    startSaving(async () => {
      if (venueId === null) return;
      // Only the venue's languages, each with both texts.
      const pick = (texts: Record<string, string>) =>
        Object.fromEntries(filledTabs.map((l) => [l, texts[l]]));
      const result = await saveFaqFromEmailAction({
        venueId,
        kind,
        itemId: kind === "venue" ? null : itemId,
        questions: pick(questions),
        answers: pick(answers),
      });
      if (!result.success) {
        toast.error(result.error ?? "Could not save the FAQ");
        return;
      }
      const venueName = venues.find((v) => v.id === venueId)?.name ?? "venue";
      const where =
        kind === "venue"
          ? venueName
          : `${KIND_LABELS[kind]} · ${items.find((i) => i.id === itemId)?.name}`;
      toast.success(
        `FAQ saved to ${where} in ${filledTabs
          .map((l) => LANGUAGE_NAMES[l] ?? l.toUpperCase())
          .join(", ")}. Customers and the AI see it now.`,
      );
      onClose();
    });

  return (
    <Dialog open={open} onOpenChange={(next) => !next && !saving && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add to FAQ</DialogTitle>
          <DialogDescription>
            A public FAQ from this email: customers see it on the website, and
            the inbox AI answers with it.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-12 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Drafting from the email…
          </div>
        ) : (
          <div className="space-y-4 text-sm">
            {(venueId === null || venues.length > 1) && (
              <label className="block">
                <span className="font-medium">Venue</span>
                <select
                  className={`${field} mt-1`}
                  value={venueId ?? ""}
                  disabled={saving}
                  onChange={(e) =>
                    chooseVenue(e.target.value ? Number(e.target.value) : null)
                  }
                >
                  <option value="">Choose a venue</option>
                  {venues.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name}
                    </option>
                  ))}
                </select>
              </label>
            )}

            <div className="grid gap-3 sm:grid-cols-[150px_1fr]">
              <label className="block">
                <span className="font-medium">Save to</span>
                <select
                  className={`${field} mt-1`}
                  value={kind}
                  disabled={saving || venueId === null}
                  onChange={(e) => {
                    setKind(e.target.value as FaqTargetKind);
                    setItemId(null);
                  }}
                >
                  {(Object.keys(KIND_LABELS) as FaqTargetKind[]).map((k) => (
                    <option key={k} value={k}>
                      {KIND_LABELS[k]}
                    </option>
                  ))}
                </select>
              </label>
              {kind !== "venue" && (
                <label className="block">
                  <span className="font-medium">{KIND_LABELS[kind]}</span>
                  <select
                    className={`${field} mt-1`}
                    value={itemId ?? ""}
                    disabled={saving || items.length === 0}
                    onChange={(e) =>
                      setItemId(e.target.value ? Number(e.target.value) : null)
                    }
                  >
                    <option value="">
                      {items.length
                        ? `Choose a ${KIND_LABELS[kind].toLowerCase()}`
                        : "None at this venue"}
                    </option>
                    {items.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>

            {/* One tab per venue language; a dot marks one that is ready. */}
            <div>
              <div
                role="tablist"
                aria-label="Languages"
                className="flex gap-1 border-b border-gray-200"
              >
                {tabs.map((l) => (
                  <button
                    key={l}
                    role="tab"
                    aria-selected={tab === l}
                    onClick={() => setTab(l)}
                    className={cn(
                      "-mb-px flex items-center gap-1.5 border-b-2 px-3 py-1.5 text-xs uppercase",
                      tab === l
                        ? "border-primary font-medium text-gray-900"
                        : "border-transparent text-gray-500 hover:text-gray-900",
                    )}
                    title={LANGUAGE_NAMES[l] ?? l}
                  >
                    {l}
                    <span
                      className={cn(
                        "h-1.5 w-1.5 rounded-full",
                        filled(l)
                          ? "bg-emerald-500"
                          : half(l)
                            ? "bg-amber-500"
                            : "bg-gray-300",
                      )}
                      aria-label={
                        filled(l)
                          ? "ready"
                          : half(l)
                            ? "question or answer missing"
                            : "empty"
                      }
                    />
                  </button>
                ))}
              </div>

              <label className="mt-3 block">
                <span className="flex items-baseline justify-between">
                  <span className="font-medium">
                    Question ({LANGUAGE_NAMES[tab] ?? tab})
                  </span>
                  <span className="font-mono text-[11px] text-gray-500">
                    {(questions[tab] ?? "").length}/{FAQ_QUESTION_LIMIT}
                  </span>
                </span>
                <input
                  className={`${field} mt-1`}
                  value={questions[tab] ?? ""}
                  maxLength={FAQ_QUESTION_LIMIT}
                  disabled={saving}
                  placeholder="Is there parking nearby?"
                  onChange={(e) =>
                    setQuestions((prev) => ({ ...prev, [tab]: e.target.value }))
                  }
                />
              </label>

              <label className="mt-3 block">
                <span className="flex items-baseline justify-between">
                  <span className="font-medium">
                    Answer ({LANGUAGE_NAMES[tab] ?? tab})
                  </span>
                  <span className="font-mono text-[11px] text-gray-500">
                    {(answers[tab] ?? "").length}/{FAQ_ANSWER_LIMIT}
                  </span>
                </span>
                <textarea
                  className={`${field} mt-1 leading-relaxed`}
                  rows={5}
                  value={answers[tab] ?? ""}
                  maxLength={FAQ_ANSWER_LIMIT}
                  disabled={saving}
                  placeholder="The answer, written for every customer"
                  onChange={(e) =>
                    setAnswers((prev) => ({ ...prev, [tab]: e.target.value }))
                  }
                />
              </label>
            </div>

            <p className="flex items-start gap-1.5 text-xs text-gray-500">
              <Sparkles className="mt-0.5 h-3 w-3 shrink-0" />
              {drafted
                ? "Drafted by AI in each of the venue's languages from the email and the staff reply - check every tab before saving."
                : draftError
                  ? `${draftError} The question is taken from the email; check it and write the answer.`
                  : "Choose a venue, then write the question and answer."}{" "}
              Saved in every language with both a question and an answer; an
              empty language can be added later on the FAQs page.
            </p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" disabled={saving} onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!canSave} onClick={save}>
            {saving && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            Save FAQ
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
